import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import api from "../lib/api";
import { toast } from "sonner";
import {
  User, Mail, Phone, MapPin, Calendar, Search,
  Loader2, X, Euro, CreditCard, CheckCircle2, XCircle, Clock as ClockIcon,
  FileText, Sparkles, ChevronRight, Trash2, Plus, Link as LinkIcon, Zap, Copy,
  Wrench, AlertTriangle, Filter, Send, RefreshCw, Undo2,
} from "lucide-react";
import FinancialSummary from "./components/FinancialSummary";
import RefundsSection from "./components/RefundsSection";

const MODE_LABELS = {
  cheque: "Chèque",
  especes: "Espèces",
  card: "Carte bancaire (en ligne)",
  monthly: "SEPA 8x (auto)",
};

const STATUS_META = {
  paid: { label: "Payée", cls: "bg-emerald-50 text-emerald-700", Icon: CheckCircle2 },
  partial: { label: "Partiel", cls: "bg-amber-50 text-amber-700", Icon: ClockIcon },
  sepa_active: { label: "SEPA en cours", cls: "bg-indigo-50 text-indigo-700", Icon: CreditCard },
  sepa_scheduled: { label: "SEPA à prélever", cls: "bg-sky-50 text-sky-700", Icon: CreditCard },
  pending: { label: "En attente", cls: "bg-stone-100 text-stone-500", Icon: ClockIcon },
  refunded: { label: "Remboursée", cls: "bg-rose-50 text-rose-700", Icon: Undo2 },
  partially_refunded: { label: "Remb. partiel", cls: "bg-orange-50 text-orange-700", Icon: Undo2 },
  failed: { label: "Échec", cls: "bg-red-50 text-red-700", Icon: XCircle },
};

const fmtEur = (n) => `${Number(n || 0).toFixed(2).replace(".", ",")} €`;
const fmtDate = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("fr-FR", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }); }
  catch { return iso; }
};

const StatusBadge = ({ status }) => {
  const m = STATUS_META[status] || STATUS_META.pending;
  const Icon = m.Icon;
  return (
    <span className={`inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-full ${m.cls}`}>
      <Icon size={12} /> {m.label}
    </span>
  );
};

const ConsentPill = ({ ok, label, highlight }) => (
  <span className={`inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full ${
    ok ? (highlight ? "bg-amber-50 text-amber-700 font-medium" : "bg-emerald-50 text-emerald-700") : "bg-stone-100 text-stone-400"
  }`}>
    {ok ? (highlight ? "★" : "✓") : "○"} {label}
  </span>
);

const ChequesSection = ({ order, onSaved }) => {
  const [cheques, setCheques] = useState(() => (order.cheques || []).map((c) => ({ type: c.type || "cheque", ...c })));
  const [saving, setSaving] = useState(false);

  const addCheque = (type = "cheque") => setCheques((prev) => [...prev, { type, number: "", holderName: "", bank: "", date: "", amount: 0, notes: "" }]);
  const removeCheque = (i) => setCheques((prev) => prev.filter((_, idx) => idx !== i));
  const patchCheque = (i, patch) => setCheques((prev) => prev.map((c, idx) => idx === i ? { ...c, ...patch } : c));

  const save = async () => {
    setSaving(true);
    try {
      const cleaned = cheques
        .filter((c) => (c.number || "").toString().trim() || Number(c.amount || 0) > 0)
        .map((c) => ({
          type: c.type || "cheque",
          number: (c.number || "").toString().trim() || (c.type === "especes" ? "ESP" : ""),
          holderName: c.holderName || "",
          bank: c.bank || "",
          date: c.date || "",
          amount: Number(c.amount || 0),
          notes: c.notes || "",
        }));
      const { data } = await api.patch(`/admin/orders/${order.id}`, { cheques: cleaned });
      toast.success("Paiements enregistrés");
      onSaved?.(data);
    } catch (e) {
      toast.error("Erreur : " + (e?.response?.data?.detail || e.message));
    } finally { setSaving(false); }
  };

  return (
    <section className="mt-6" data-testid="order-cheques-section">
      <h3 className="font-serif-display text-lg text-stone-800 mb-3 flex items-center gap-2">
        <FileText size={16} className="text-deep-rose"/> Paiements complémentaires (chèque / espèces)
      </h3>
      <div className="bg-white border border-stone-200 rounded-2xl p-4">
        {cheques.length === 0 && (
          <div className="text-stone-500 text-sm italic mb-3">Aucun paiement complémentaire enregistré.</div>
        )}
        <div className="space-y-2">
          {cheques.map((c, i) => {
            const isCash = (c.type || "cheque") === "especes";
            return (
              <div key={i} className="grid grid-cols-1 sm:grid-cols-12 gap-2 items-center bg-stone-50 rounded-lg p-2" data-testid={`cheque-row-${i}`}>
                <select
                  value={c.type || "cheque"}
                  onChange={(e) => patchCheque(i, { type: e.target.value })}
                  data-testid={`cheque-${i}-type`}
                  className="sm:col-span-2 bg-white border border-stone-200 rounded px-2 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blush"
                >
                  <option value="cheque">Chèque</option>
                  <option value="especes">Espèces</option>
                </select>
                <input
                  value={c.number || ""}
                  onChange={(e) => patchCheque(i, { number: e.target.value })}
                  placeholder={isCash ? "Réf. (opt.)" : "N° chèque *"}
                  data-testid={`cheque-${i}-number`}
                  className="sm:col-span-2 bg-white border border-stone-200 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blush"
                />
                <input
                  value={c.holderName || ""}
                  onChange={(e) => patchCheque(i, { holderName: e.target.value })}
                  placeholder={isCash ? "Reçu de" : "Nom du porteur"}
                  data-testid={`cheque-${i}-holder`}
                  className="sm:col-span-3 bg-white border border-stone-200 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blush"
                />
                {isCash ? (
                  <div className="sm:col-span-2 text-xs text-stone-400 italic px-2">—</div>
                ) : (
                  <input
                    value={c.bank || ""}
                    onChange={(e) => patchCheque(i, { bank: e.target.value })}
                    placeholder="Banque"
                    data-testid={`cheque-${i}-bank`}
                    className="sm:col-span-2 bg-white border border-stone-200 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blush"
                  />
                )}
                <input
                  type="date"
                  value={c.date || ""}
                  onChange={(e) => patchCheque(i, { date: e.target.value })}
                  data-testid={`cheque-${i}-date`}
                  className="sm:col-span-1 bg-white border border-stone-200 rounded px-2 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blush"
                />
                <input
                  type="number" step="0.01"
                  value={c.amount || ""}
                  onChange={(e) => patchCheque(i, { amount: e.target.value })}
                  placeholder="Montant €"
                  data-testid={`cheque-${i}-amount`}
                  className="sm:col-span-1 bg-white border border-stone-200 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blush"
                />
                <button
                  type="button"
                  onClick={() => removeCheque(i)}
                  data-testid={`cheque-${i}-remove`}
                  className="sm:col-span-1 text-xs bg-red-50 hover:bg-red-100 text-red-700 px-2 py-2 rounded"
                >Suppr.</button>
              </div>
            );
          })}
        </div>
        <div className="mt-3 flex flex-wrap items-center justify-between gap-2">
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => addCheque("cheque")}
              data-testid="cheque-add-btn"
              className="text-xs bg-blush-light hover:bg-blush text-deep-rose px-3 py-1.5 rounded-full"
            >+ Ajouter un chèque</button>
            <button
              type="button"
              onClick={() => addCheque("especes")}
              data-testid="cash-add-btn"
              className="text-xs bg-emerald-50 hover:bg-emerald-100 text-emerald-700 px-3 py-1.5 rounded-full"
            >+ Ajouter des espèces</button>
          </div>
          <button
            type="button"
            onClick={save}
            disabled={saving}
            data-testid="cheque-save-btn"
            className="text-xs bg-coral hover:brightness-105 text-white px-4 py-1.5 rounded-full disabled:opacity-50"
          >{saving ? "Enregistrement..." : "Enregistrer"}</button>
        </div>
        {cheques.length > 0 && (
          <div className="mt-3 pt-3 border-t border-stone-100 text-xs text-stone-500">
            Total complémentaire saisi : <strong className="text-stone-800">{fmtEur(cheques.reduce((s, c) => s + Number(c.amount || 0), 0))}</strong>
          </div>
        )}
      </div>
    </section>
  );
};

export const OrderDetailPage = () => {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(false);
  const [showAddSlots, setShowAddSlots] = useState(false);
  const [showPayLink, setShowPayLink] = useState(false);
  const [sendingSepa, setSendingSepa] = useState(false);
  const [removingId, setRemovingId] = useState(null);
  const [recomputing, setRecomputing] = useState(false);
  const [sepaResult, setSepaResult] = useState(null);

  const reloadOrder = async () => {
    try {
      const { data } = await api.get(`/admin/orders/${orderId}`);
      setOrder(data);
    } catch { toast.error("Erreur"); }
  };

  const sendSepaLink = async () => {
    const solde = Number(order?.remainingAmount ?? order?.totalFinal ?? 0);
    if (!window.confirm(`Créer et envoyer un lien de mandat SEPA sur le SOLDE de ${fmtEur(solde)} (prélèvement en 8×) à la famille ?\n\nLe lien sera envoyé par email ET affiché à l'écran. La commande passera en mode SEPA.`)) return;
    setSendingSepa(true);
    try {
      const { data } = await api.post(`/admin/orders/${orderId}/send-sepa-link`);
      setSepaResult(data);
      if (data.emailed) toast.success(data.message);
      else toast.info(data.message);
      await reloadOrder();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    } finally { setSendingSepa(false); }
  };

  const copySepaLink = async () => {
    try {
      await navigator.clipboard.writeText(sepaResult.url);
      toast.success("Lien copié dans le presse-papier");
    } catch { toast.error("Impossible de copier"); }
  };

  const removeEnrollment = async (enrollmentId, label) => {
    if (!window.confirm(`Retirer le cours « ${label} » de cette commande ?\n\nLe montant total sera recalculé automatiquement (remise multi-cours ajustée).`)) return;
    setRemovingId(enrollmentId);
    try {
      await api.delete(`/admin/orders/${orderId}/enrollments/${enrollmentId}`);
      toast.success("Cours retiré — total recalculé");
      await reloadOrder();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    } finally { setRemovingId(null); }
  };

  const recomputeTotal = async () => {
    setRecomputing(true);
    try {
      await api.post(`/admin/orders/${orderId}/recompute`);
      toast.success("Total recalculé depuis les cours actuels");
      await reloadOrder();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    } finally { setRecomputing(false); }
  };

  useEffect(() => {
    setLoading(true);
    api.get(`/admin/orders/${orderId}`)
      .then(({ data }) => setOrder(data))
      .catch(() => toast.error("Impossible de charger la commande"))
      .finally(() => setLoading(false));
  }, [orderId]);

  const deleteOrder = async () => {
    if (!order) return;
    if (!window.confirm(`Supprimer définitivement la commande ${order.orderNumber || order.id.slice(0, 8)} ? Les inscriptions associées seront aussi supprimées. Cette action est irréversible.`)) return;
    setDeleting(true);
    try {
      await api.delete(`/admin/orders/${orderId}`);
      toast.success(`Commande ${order.orderNumber || ""} supprimée`);
      navigate("/admin/orders");
    } catch (e) {
      toast.error("Erreur : " + (e?.response?.data?.detail || e.message));
      setDeleting(false);
    }
  };

  return (
    <div data-testid="order-detail-page">
      <button onClick={() => navigate("/admin/orders")} className="inline-flex items-center gap-1 text-stone-600 hover:text-deep-rose mb-4 text-sm">
        ← Retour aux commandes
      </button>
      {loading ? (
        <div className="p-12 text-center text-stone-500"><Loader2 className="mx-auto animate-spin" /> Chargement…</div>
      ) : order ? (
        <div className="bg-white rounded-3xl shadow-sm p-6 sm:p-8">
            <div className="pr-10">
              <div className="text-xs uppercase tracking-widest text-stone-500">Commande</div>
              <h2 className="font-serif-display text-2xl text-stone-800 mt-1">{order.parentName || "—"}</h2>
              <div className="flex items-center gap-3 flex-wrap mt-2">
                <span className="text-stone-800 text-sm font-mono font-medium bg-blush-light/70 rounded-full px-2 py-0.5">{order.orderNumber || `#${(order.id || "").slice(0, 8)}`}</span>
                <span className="text-stone-500 text-sm">· {fmtDate(order.createdAt)}</span>
                <StatusBadge status={order.paymentStatus} />
              </div>
            </div>

            {/* Coordonnées famille */}
            <section className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm bg-cream rounded-2xl p-4">
              {order.familyEmail && <div className="flex items-center gap-2 text-stone-700 min-w-0"><Mail size={14} className="text-deep-rose shrink-0"/> <a href={`mailto:${order.familyEmail}`} className="truncate hover:text-deep-rose">{order.familyEmail}</a></div>}
              {order.phone && <div className="flex items-center gap-2 text-stone-700"><Phone size={14} className="text-deep-rose shrink-0"/> {order.phone}</div>}
              {(order.address || order.city) && <div className="flex items-center gap-2 text-stone-700 sm:col-span-2"><MapPin size={14} className="text-deep-rose shrink-0"/> {[order.address, order.postalCode, order.city].filter(Boolean).join(", ")}</div>}
              {order.season && <div className="flex items-center gap-2 text-stone-700"><Calendar size={14} className="text-deep-rose shrink-0"/> Saison {order.season}</div>}
            </section>

            {/* Élèves et créneaux */}
            <section className="mt-6">
              <h3 className="font-serif-display text-lg text-stone-800 mb-3">Élèves et créneaux</h3>
              <div className="space-y-2">
                {(order.enrollments || []).map((e) => (
                  <div key={e.id} className="bg-white border border-stone-200 rounded-xl p-3">
                    <div className="flex items-start justify-between gap-2 flex-wrap">
                      <div className="min-w-0 flex-1">
                        <div className="font-medium text-stone-800 flex items-center gap-2"><User size={14} className="text-deep-rose"/> {e.studentFirstName || "—"}</div>
                        <div className="text-stone-500 text-xs mt-1">
                          {e.slotTitle || e.courseName || "—"}
                          {e.slotDay && ` · ${e.slotDay} ${e.slotStartTime}–${e.slotEndTime}`}
                        </div>
                      </div>
                      <div className="text-right shrink-0">
                        <div className="text-stone-800 font-serif-display">{fmtEur(e.slotPrice != null ? e.slotPrice : e.finalAmount)}</div>
                        <div className="mt-1"><StatusBadge status={e.paymentStatus} /></div>
                        <button
                          onClick={() => removeEnrollment(e.id, e.slotTitle || e.courseName || "ce cours")}
                          disabled={removingId === e.id}
                          data-testid={`order-remove-enrollment-${e.id}`}
                          className="mt-2 inline-flex items-center gap-1 text-xs text-red-600 hover:text-white hover:bg-red-600 border border-red-200 hover:border-red-600 px-2.5 py-1 rounded-full transition-colors disabled:opacity-50"
                        >
                          {removingId === e.id ? <Loader2 size={11} className="animate-spin"/> : <Trash2 size={11}/>}
                          Retirer
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            {/* Détail tarif */}
            {order.quote && (
              <section className="mt-6">
                <h3 className="font-serif-display text-lg text-stone-800 mb-3">Détail du tarif</h3>
                <div className="bg-blush-light/40 rounded-2xl p-4 space-y-2 text-sm">
                  <div className="flex justify-between"><span className="text-stone-600">Sous-total créneaux</span><span className="text-stone-800">{fmtEur(order.quote.subtotal)}</span></div>
                  {order.quote.discountAmount > 0 && (
                    <div className="flex justify-between text-emerald-700"><span>Remise multi-cours (par élève)</span><span>− {fmtEur(order.quote.discountAmount)}</span></div>
                  )}
                  {order.quote.cotisation > 0 && (
                    <div className="flex justify-between"><span className="text-stone-600">Cotisation asso ({order.quote.cotisationIsLocal ? "local" : "hors commune"}{order.quote.cotisationStudents > 1 ? ` × ${order.quote.cotisationStudents}` : ""})</span><span className="text-stone-800">{fmtEur(order.quote.cotisation)}</span></div>
                  )}
                  {order.quote.licenseFFDTotal > 0 && (
                    <div className="flex justify-between"><span className="text-stone-600">Licence FFD ({order.quote.licenseFFDStudents} × {fmtEur(order.quote.licenseFFDPerStudent)})</span><span className="text-stone-800">{fmtEur(order.quote.licenseFFDTotal)}</span></div>
                  )}
                  {order.quote.applicationFee > 0 && (
                    <div className="flex justify-between"><span className="text-stone-600">{order.quote.applicationFeeLabel || "Frais de traitement"}</span><span className="text-stone-800">{fmtEur(order.quote.applicationFee)}</span></div>
                  )}
                  <div className="pt-2 mt-2 border-t border-stone-200 flex justify-between items-center">
                    <span className="font-serif-display text-stone-800">Total</span>
                    <span className="font-serif-display text-xl text-deep-rose">{fmtEur(order.quote.total)}</span>
                  </div>
                  {order.totalPaid > 0 && (
                    <div className="text-xs text-stone-500 pt-1">Déjà encaissé : <strong className="text-emerald-700">{fmtEur(order.totalPaid)}</strong> sur {fmtEur(order.totalFinal)}</div>
                  )}
                </div>
              </section>
            )}

            {/* Paiement */}
            <section className="mt-6">
              <h3 className="font-serif-display text-lg text-stone-800 mb-3">Paiement</h3>
              <div className="bg-white border border-stone-200 rounded-2xl p-4 text-sm space-y-3">
                <div className="flex items-center gap-2"><CreditCard size={14} className="text-deep-rose"/> Mode : <strong>{MODE_LABELS[order.paymentMode] || order.paymentMode}</strong></div>

                {/* Récap encaissements — même composant partagé partout dans le BO */}
                <FinancialSummary
                  attendu={order.totalFinal}
                  encaisse={order.totalPaid}
                  reste={order.remainingAmount}
                  dense
                  testid="order-detail-financial-summary"
                />
                {((order.stripePaid || 0) > 0 || (order.manualPaid || 0) > 0) && (
                  <div className="text-[11px] text-stone-500 pt-1">
                    Détails encaissement :
                    {(order.stripePaid || 0) > 0 && <> Stripe {fmtEur(order.stripePaid)}</>}
                    {(order.stripePaid || 0) > 0 && (order.manualPaid || 0) > 0 && " · "}
                    {(order.manualPaid || 0) > 0 && <>Chèques/espèces {fmtEur(order.manualPaid)}</>}
                  </div>
                )}

                {order.transaction && (
                  <div className="text-stone-500 text-xs space-y-0.5 pt-2 border-t border-stone-100">
                    <div>Session Stripe : <code className="bg-stone-50 px-1 rounded">{order.transaction.stripeSessionId?.slice(-16) || "—"}</code></div>
                    <div>Statut Stripe : {order.transaction.status} · {order.transaction.paymentStatus}</div>
                    <div>Montant encaissé par Stripe : <strong className="text-stone-700">{fmtEur(order.transaction.amount)}</strong></div>
                    {order.transaction.mode === "monthly" && (
                      <>
                        <div>SEPA : {order.transaction.iterationsPaid || 0}/{order.transaction.iterations || 8} prélèvements</div>
                        <div className="pt-2">
                          <Link
                            to={`/admin/sepa-diagnostic?tx=${order.transaction.id || ""}`}
                            data-testid="order-sepa-diag-link"
                            className="inline-flex items-center gap-1.5 bg-blush-light hover:bg-blush text-deep-rose px-3 py-1.5 rounded-full text-xs font-medium"
                          >
                            <Wrench size={12} /> Ouvrir le diagnostic SEPA
                          </Link>
                        </div>
                      </>
                    )}
                  </div>
                )}
              </div>
            </section>

            {/* Paiements complémentaires (chèques + espèces) — toujours accessibles pour rattraper un solde */}
            <ChequesSection order={order} onSaved={(updated) => setOrder({ ...order, ...updated })} />

            {/* Remboursements (Stripe + hors Stripe) */}
            <RefundsSection order={order} onChanged={reloadOrder} />

            {/* Actions BO : ajouter des cours + générer un lien Stripe */}
            <section className="mt-6">
              <h3 className="font-serif-display text-lg text-stone-800 mb-3 flex items-center gap-2"><Zap size={16} className="text-deep-rose"/> Éditer la commande</h3>
              <div className="bg-white border border-stone-200 rounded-2xl p-4 flex flex-wrap items-center gap-3">
                <button
                  onClick={() => setShowAddSlots(true)}
                  data-testid="order-add-slots-btn"
                  className="inline-flex items-center gap-1.5 bg-blush-light hover:bg-blush text-deep-rose px-4 py-2 rounded-full text-sm"
                >
                  <Plus size={14}/> Ajouter des cours
                </button>
                <button
                  onClick={() => setShowPayLink(true)}
                  data-testid="order-payment-link-btn"
                  className="inline-flex items-center gap-1.5 bg-coral hover:brightness-105 text-white px-4 py-2 rounded-full text-sm"
                >
                  <LinkIcon size={14}/> Générer un lien Stripe
                </button>
                <button
                  onClick={sendSepaLink}
                  disabled={sendingSepa}
                  data-testid="order-send-sepa-btn"
                  className="inline-flex items-center gap-1.5 bg-deep-rose hover:brightness-110 text-white px-4 py-2 rounded-full text-sm disabled:opacity-50"
                >
                  {sendingSepa ? <Loader2 size={14} className="animate-spin"/> : <Send size={14}/>}
                  Envoyer le lien SEPA (8x)
                </button>
                <button
                  onClick={recomputeTotal}
                  disabled={recomputing}
                  data-testid="order-recompute-btn"
                  className="inline-flex items-center gap-1.5 bg-white border border-stone-300 hover:border-deep-rose text-stone-700 px-4 py-2 rounded-full text-sm disabled:opacity-50"
                >
                  {recomputing ? <Loader2 size={14} className="animate-spin"/> : <RefreshCw size={14}/>}
                  Recalculer le total
                </button>
                <p className="text-stone-500 text-xs w-full">
                  L&apos;ajout d&apos;un cours recalcule automatiquement la remise multi-cours (10% → 15% dès 3 cours par élève).
                  Le lien Stripe est pré-rempli avec le reste à percevoir mais reste modifiable.
                  Le lien SEPA (8x) est calculé sur le <strong>solde restant dû</strong>, envoyé par email et affiché ci-dessous.
                </p>
                {sepaResult && (
                  <div className="w-full mt-2 bg-emerald-50 border border-emerald-200 rounded-xl p-4" data-testid="sepa-link-result">
                    <div className="text-emerald-800 font-medium text-sm flex items-center gap-1.5">
                      <Send size={14}/> {sepaResult.emailed ? "Lien SEPA envoyé par email ✉️" : "Lien SEPA prêt à transmettre"}
                    </div>
                    {sepaResult.amount != null && (
                      <div className="text-emerald-800 text-xs mt-1">
                        Solde prélevé : <strong>{fmtEur(sepaResult.amount)}</strong> en 8× ({fmtEur(sepaResult.monthlyAmount)}/mois)
                      </div>
                    )}
                    <div className="mt-2 flex items-center gap-2 bg-white border border-emerald-100 rounded-lg p-2 text-xs">
                      <input readOnly value={sepaResult.url || ""} data-testid="sepa-link-url" className="flex-1 bg-transparent outline-none text-emerald-900 truncate"/>
                      <button onClick={copySepaLink} data-testid="sepa-link-copy" className="shrink-0 inline-flex items-center gap-1 bg-emerald-700 hover:bg-emerald-800 text-white px-3 py-1 rounded-full text-xs"><Copy size={12}/> Copier</button>
                    </div>
                  </div>
                )}
              </div>
            </section>

            {/* Consentements */}
            {order.consents && (
              <section className="mt-6">
                <h3 className="font-serif-display text-lg text-stone-800 mb-3 flex items-center gap-2"><FileText size={16} className="text-deep-rose"/> Consentements</h3>
                <div className="flex flex-wrap gap-2">
                  <ConsentPill ok={order.consents.cgv} label="CGV" />
                  <ConsentPill ok={order.consents.reglement} label="Règlement" />
                  <ConsentPill ok={order.consents.image} label="Droit image" />
                  <ConsentPill ok={order.consents.whatsapp} label="WhatsApp" />
                  <ConsentPill ok={order.consents.cseAttestation} label="Attestation CSE" highlight />
                </div>
                {order.consents.acceptedAt && (
                  <div className="text-xs text-stone-500 mt-2">Accepté le {fmtDate(order.consents.acceptedAt)}</div>
                )}
              </section>
            )}

            {/* Message */}
            {order.message && (
              <section className="mt-6">
                <h3 className="font-serif-display text-lg text-stone-800 mb-2 flex items-center gap-2"><Sparkles size={16} className="text-deep-rose"/> Message de la famille</h3>
                <div className="bg-cream rounded-2xl p-4 text-stone-700 text-sm whitespace-pre-line">{order.message}</div>
              </section>
            )}

            {/* Zone dangereuse — suppression de la commande */}
            <section className="mt-8 pt-6 border-t border-red-100">
              <div className="bg-red-50/50 rounded-2xl p-4">
                <h3 className="text-red-800 font-medium text-sm mb-1">Zone dangereuse</h3>
                <p className="text-red-600 text-xs mb-3">Supprime la commande et toutes les inscriptions associées. Les transactions Stripe éventuelles sont conservées pour la traçabilité comptable. La famille et les élèves ne sont pas touchés.</p>
                <button
                  onClick={deleteOrder}
                  disabled={deleting}
                  data-testid="order-delete-btn"
                  className="inline-flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white text-sm px-4 py-2 rounded-full disabled:opacity-50 transition"
                >
                  {deleting ? <Loader2 size={14} className="animate-spin"/> : <Trash2 size={14}/>}
                  {deleting ? "Suppression..." : "Supprimer cette commande"}
                </button>
              </div>
            </section>
          </div>
        ) : null}
      {showAddSlots && order && (
        <AddSlotsModal
          order={order}
          onClose={() => setShowAddSlots(false)}
          onSaved={async () => { setShowAddSlots(false); await reloadOrder(); }}
        />
      )}
      {showPayLink && order && (
        <PaymentLinkModal
          order={order}
          onClose={() => setShowPayLink(false)}
        />
      )}
    </div>
  );
};

// ---- Modal : Ajouter des cours à une commande ----
const AddSlotsModal = ({ order, onClose, onSaved }) => {
  const [students, setStudents] = useState([]);
  const [slots, setSlots] = useState([]);
  const [studentId, setStudentId] = useState("");
  const [selected, setSelected] = useState([]);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [mode, setMode] = useState("existing"); // 'existing' | 'new'
  const [newStudent, setNewStudent] = useState({ firstName: "", lastName: "", birthDate: "" });

  useEffect(() => {
    (async () => {
      let famOk = false, contentOk = false;
      try {
        const { data: fam } = await api.get(`/admin/families/${order.familyId}`);
        setStudents(fam.students || []);
        // Pre-select student from the first enrollment
        const firstEnroll = (fam.enrollments || []).find((e) => (order.enrollmentIds || []).includes(e.id));
        setStudentId(firstEnroll?.studentId || (fam.students?.[0]?.id ?? ""));
        famOk = true;
      } catch (e) { /* handled below */ }
      try {
        const { data: content } = await api.get("/content");
        setSlots((content.scheduleSlots || []).filter((s) => (s.price || 0) > 0));
        contentOk = true;
      } catch (e) { /* handled below */ }
      if (!famOk && !contentOk) toast.error("Erreur de chargement");
      else if (!famOk) toast.error("Impossible de charger la famille");
      else if (!contentOk) toast.error("Impossible de charger les créneaux");
      setLoading(false);
    })();
  }, [order.familyId, order.enrollmentIds]);

  // Fetch a live quote preview whenever selection changes
  useEffect(() => {
    if (selected.length === 0) { setPreview(null); return; }
    const targetName = mode === "new" ? (newStudent.firstName || "Nouvel élève") : (students.find((s) => s.id === studentId)?.firstName || "");
    const targetKey = mode === "new" ? "__new__" : studentId;
    if (!targetKey) { setPreview(null); return; }
    const t = setTimeout(async () => {
      try {
        // Compose the full slot list per student (existing + newly selected)
        const existingByStudent = {};
        for (const eid of (order.enrollmentIds || [])) {
          const enr = (order.enrollments || []).find((e) => e.id === eid);
          if (!enr) continue;
          const sid = enr.studentId;
          if (!existingByStudent[sid]) existingByStudent[sid] = { firstName: enr.studentFirstName, slotIds: [] };
          if (enr.slotId) existingByStudent[sid].slotIds.push(enr.slotId);
        }
        // Add new selected slots to the target student
        if (!existingByStudent[targetKey]) existingByStudent[targetKey] = { firstName: targetName, slotIds: [] };
        existingByStudent[targetKey].slotIds = [...new Set([...existingByStudent[targetKey].slotIds, ...selected])];
        const payload = {
          students: Object.values(existingByStudent).map((s) => ({ firstName: s.firstName, slotIds: s.slotIds })),
          postalCode: order.postalCode || "",
        };
        const { data } = await api.post("/inscriptions/quote", payload);
        setPreview(data);
      } catch (e) { /* silently ignore preview errors */ }
    }, 250);
    return () => clearTimeout(t);
  }, [studentId, selected, order, students, mode, newStudent.firstName]);

  const toggleSlot = (sid) => setSelected((prev) => prev.includes(sid) ? prev.filter((x) => x !== sid) : [...prev, sid]);

  const submit = async () => {
    if (mode === "existing" && !studentId) { toast.error("Sélectionnez un élève."); return; }
    if (mode === "new") {
      if (!newStudent.firstName.trim() || !newStudent.lastName.trim()) {
        toast.error("Prénom et nom du nouvel élève requis."); return;
      }
    }
    if (selected.length === 0) { toast.error("Sélectionnez au moins un créneau."); return; }
    setSubmitting(true);
    try {
      const body = mode === "new"
        ? { newStudent: {
            firstName: newStudent.firstName.trim(),
            lastName: newStudent.lastName.trim(),
            birthDate: newStudent.birthDate || null,
          }, slotIds: selected }
        : { studentId, slotIds: selected };
      await api.post(`/admin/orders/${order.id}/add-slots`, body);
      toast.success(mode === "new" ? "Nouvel élève ajouté avec ses cours" : "Cours ajoutés à la commande");
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    } finally { setSubmitting(false); }
  };

  const currentTotal = Number(order.totalFinal || 0);
  const newTotal = preview?.total || currentTotal;
  const delta = round2(newTotal - currentTotal);

  return (
    <div className="fixed inset-0 z-50 bg-stone-900/70 backdrop-blur-sm flex items-start justify-center p-4 sm:p-6 overflow-y-auto" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} data-testid="add-slots-modal" className="bg-white rounded-3xl shadow-xl max-w-2xl w-full my-8 p-6 sm:p-8 relative">
        <button onClick={onClose} className="absolute top-4 right-4 p-2 rounded-full bg-stone-100 hover:bg-stone-200 text-stone-600"><X size={18}/></button>
        <div className="text-xs uppercase tracking-widest text-deep-rose">Éditer la commande</div>
        <h3 className="font-serif-display text-2xl text-stone-800 mt-1">Ajouter des cours</h3>
        <p className="text-stone-500 text-sm mt-1">La remise multi-cours (10% → 15%) est recalculée automatiquement sur le cours le moins cher de l&apos;élève.</p>

        {loading ? <div className="text-stone-500 p-8 text-center"><Loader2 className="mx-auto animate-spin"/></div> : (
          <>
            <div className="mt-5">
              <div className="inline-flex bg-cream border border-blush-light rounded-full p-1 text-xs" role="tablist">
                <button
                  onClick={() => { setMode("existing"); setSelected([]); }}
                  data-testid="add-slots-mode-existing"
                  className={`px-4 py-1.5 rounded-full transition-colors ${mode === "existing" ? "bg-deep-rose text-white" : "text-stone-600 hover:text-stone-800"}`}
                >
                  Élève existant
                </button>
                <button
                  onClick={() => { setMode("new"); setSelected([]); }}
                  data-testid="add-slots-mode-new"
                  className={`px-4 py-1.5 rounded-full transition-colors ${mode === "new" ? "bg-deep-rose text-white" : "text-stone-600 hover:text-stone-800"}`}
                >
                  + Nouvel élève
                </button>
              </div>
            </div>

            {mode === "existing" ? (
              <div className="mt-4">
                <label className="text-xs uppercase tracking-widest text-stone-500">Élève</label>
                <select value={studentId} onChange={(e) => { setStudentId(e.target.value); setSelected([]); }} data-testid="add-slots-student" className="mt-1 w-full bg-cream border border-blush-light rounded-xl px-3 py-2 text-sm">
                  {students.map((s) => <option key={s.id} value={s.id}>{s.firstName} {s.lastName}</option>)}
                </select>
              </div>
            ) : (
              <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-2">
                <div>
                  <label className="text-xs uppercase tracking-widest text-stone-500">Prénom</label>
                  <input value={newStudent.firstName} onChange={(e) => setNewStudent({ ...newStudent, firstName: e.target.value })} data-testid="add-slots-new-firstname" className="mt-1 w-full bg-cream border border-blush-light rounded-xl px-3 py-2 text-sm" />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-widest text-stone-500">Nom</label>
                  <input value={newStudent.lastName} onChange={(e) => setNewStudent({ ...newStudent, lastName: e.target.value })} data-testid="add-slots-new-lastname" className="mt-1 w-full bg-cream border border-blush-light rounded-xl px-3 py-2 text-sm" />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-widest text-stone-500">Naissance</label>
                  <input type="date" value={newStudent.birthDate} onChange={(e) => setNewStudent({ ...newStudent, birthDate: e.target.value })} data-testid="add-slots-new-birth" className="mt-1 w-full bg-cream border border-blush-light rounded-xl px-3 py-2 text-sm" />
                </div>
              </div>
            )}

            <div className="mt-4">
              <label className="text-xs uppercase tracking-widest text-stone-500">Créneaux à ajouter ({selected.length} sélectionné{selected.length > 1 ? "s" : ""})</label>
              <div className="mt-1 max-h-60 overflow-y-auto rounded-xl border border-stone-200 divide-y divide-stone-100">
                {slots.map((s) => {
                  const active = selected.includes(s.id);
                  return (
                    <button key={s.id} type="button" onClick={() => toggleSlot(s.id)} data-testid={`add-slots-slot-${s.id}`}
                      className={`w-full flex items-center justify-between gap-2 px-3 py-2 text-left text-sm ${active ? "bg-blush-light/50 text-deep-rose" : "hover:bg-stone-50 text-stone-700"}`}>
                      <span className="min-w-0 truncate">{s.title} — {s.day} {s.startTime}-{s.endTime}</span>
                      <span className="text-xs shrink-0">{fmtEur(s.price)}</span>
                    </button>
                  );
                })}
                {slots.length === 0 && <div className="p-4 text-center text-stone-400 text-sm">Aucun créneau tarifé disponible.</div>}
              </div>
            </div>

            {preview && (
              <div className="mt-4 bg-cream rounded-xl p-4 text-sm">
                <div className="flex justify-between"><span className="text-stone-500">Sous-total cours</span><span>{fmtEur(preview.coursesTotal)}</span></div>
                {preview.discountAmount > 0 && (
                  <div className="flex justify-between text-emerald-700"><span>Remise multi-cours ({preview.discountPercent}%)</span><span>-{fmtEur(preview.discountAmount)}</span></div>
                )}
                <div className="flex justify-between"><span className="text-stone-500">Cotisation</span><span>{fmtEur(preview.cotisation || 0)}</span></div>
                <div className="flex justify-between"><span className="text-stone-500">Licence FFD</span><span>{fmtEur(preview.licenseFFDTotal || 0)}</span></div>
                <div className="flex justify-between"><span className="text-stone-500">Frais dossier</span><span>{fmtEur(preview.applicationFee || 0)}</span></div>
                <div className="mt-2 pt-2 border-t border-stone-200 flex justify-between font-medium">
                  <span className="text-stone-800">Nouveau total</span>
                  <span className="font-serif-display text-lg text-deep-rose">{fmtEur(newTotal)}</span>
                </div>
                {delta > 0 && (
                  <div className="text-amber-700 text-xs mt-1">
                    +{fmtEur(delta)} à percevoir en supplément (ancien total : {fmtEur(currentTotal)})
                  </div>
                )}
              </div>
            )}

            <div className="mt-6 flex justify-end gap-3">
              <button onClick={onClose} className="px-5 py-2 rounded-full bg-stone-100 text-stone-700 text-sm">Annuler</button>
              <button onClick={submit} disabled={submitting || selected.length === 0} data-testid="add-slots-submit"
                className="px-6 py-2 rounded-full bg-coral text-white text-sm tracking-widest uppercase disabled:opacity-50 inline-flex items-center gap-2">
                {submitting ? <><Loader2 size={14} className="animate-spin"/> Ajout…</> : <>Ajouter à la commande</>}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

// ---- Modal : Générer un lien de paiement Stripe ----
const PaymentLinkModal = ({ order, onClose }) => {
  const [amount, setAmount] = useState(order.remainingAmount > 0 ? Number(order.remainingAmount) : Number(order.totalFinal || 0));
  const [description, setDescription] = useState(`Complément commande ${order.orderNumber || ""}`);
  const [sendEmail, setSendEmail] = useState(true);
  const [link, setLink] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const generate = async () => {
    if (!amount || Number(amount) <= 0) { toast.error("Montant invalide"); return; }
    setSubmitting(true);
    try {
      const { data } = await api.post(`/admin/orders/${order.id}/payment-link`, { amount: Number(amount), description, sendEmail });
      setLink(data);
      toast.success(data.emailSent ? "Lien Stripe envoyé par email à la famille" : "Lien Stripe généré");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur Stripe");
    } finally { setSubmitting(false); }
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(link.url);
      toast.success("Lien copié dans le presse-papier");
    } catch { toast.error("Impossible de copier"); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-stone-900/70 backdrop-blur-sm flex items-start justify-center p-4 sm:p-6 overflow-y-auto" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} data-testid="payment-link-modal" className="bg-white rounded-3xl shadow-xl max-w-lg w-full my-8 p-6 sm:p-8 relative">
        <button onClick={onClose} className="absolute top-4 right-4 p-2 rounded-full bg-stone-100 hover:bg-stone-200 text-stone-600"><X size={18}/></button>
        <div className="text-xs uppercase tracking-widest text-deep-rose">Éditer la commande</div>
        <h3 className="font-serif-display text-2xl text-stone-800 mt-1">Lien de paiement Stripe</h3>
        <p className="text-stone-500 text-sm mt-1">Génère une URL Stripe que vous pouvez transmettre à la famille par email ou SMS.</p>

        {!link ? (
          <>
            <div className="mt-5">
              <label className="text-xs uppercase tracking-widest text-stone-500">Montant (€) <span className="text-stone-400 normal-case">— pré-rempli avec le reste à percevoir</span></label>
              <input type="number" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="paylink-amount"
                className="mt-1 w-full bg-cream border border-blush-light rounded-xl px-3 py-2 text-sm"/>
            </div>
            <div className="mt-3">
              <label className="text-xs uppercase tracking-widest text-stone-500">Libellé (visible par la famille)</label>
              <input value={description} onChange={(e) => setDescription(e.target.value)} data-testid="paylink-desc"
                className="mt-1 w-full bg-cream border border-blush-light rounded-xl px-3 py-2 text-sm"/>
            </div>
            <label className="mt-4 flex items-center gap-2 text-sm text-stone-700 cursor-pointer">
              <input type="checkbox" checked={sendEmail} onChange={(e) => setSendEmail(e.target.checked)} data-testid="paylink-sendemail" className="w-4 h-4 accent-coral"/>
              <span>Envoyer automatiquement le lien par email à la famille</span>
            </label>
            <div className="mt-6 flex justify-end gap-3">
              <button onClick={onClose} className="px-5 py-2 rounded-full bg-stone-100 text-stone-700 text-sm">Annuler</button>
              <button onClick={generate} disabled={submitting} data-testid="paylink-generate"
                className="px-6 py-2 rounded-full bg-coral text-white text-sm tracking-widest uppercase disabled:opacity-50 inline-flex items-center gap-2">
                {submitting ? <><Loader2 size={14} className="animate-spin"/> Génération…</> : <>Générer le lien</>}
              </button>
            </div>
          </>
        ) : (
          <div className="mt-5">
            <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4">
              <div className="text-emerald-800 font-medium text-sm">
                {link.emailSent ? "Lien envoyé par email à la famille ✉️" : "Lien prêt à envoyer"}
              </div>
              <div className="mt-2 flex items-center gap-2 bg-white border border-emerald-100 rounded-lg p-2 text-xs">
                <input readOnly value={link.url} data-testid="paylink-url" className="flex-1 bg-transparent outline-none text-emerald-900 truncate"/>
                <button onClick={copy} className="shrink-0 inline-flex items-center gap-1 bg-emerald-700 hover:bg-emerald-800 text-white px-3 py-1 rounded-full text-xs" data-testid="paylink-copy"><Copy size={12}/> Copier</button>
              </div>
              <div className="text-emerald-800 text-xs mt-2">Montant : <strong>{fmtEur(link.amount)}</strong>. Une fois payé, le solde de la commande se mettra à jour automatiquement.</div>
            </div>
            <div className="mt-6 flex justify-end">
              <button onClick={onClose} className="px-6 py-2 rounded-full bg-coral text-white text-sm">Fermer</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const round2 = (n) => Math.round((Number(n) + Number.EPSILON) * 100) / 100;

const OrdersAdmin = () => {
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [modeFilter, setModeFilter] = useState("all");

  useEffect(() => {
    api.get("/admin/orders")
      .then(({ data }) => setOrders(data || []))
      .catch(() => toast.error("Erreur au chargement"))
      .finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(() => {
    const n = q.trim().toLowerCase();
    return orders.filter((o) => {
      if (n && !(
        (o.parentName || "").toLowerCase().includes(n) ||
        (o.familyEmail || "").toLowerCase().includes(n) ||
        (o.city || "").toLowerCase().includes(n) ||
        (o.orderNumber || "").toLowerCase().includes(n) ||
        (o.id || "").toLowerCase().includes(n)
      )) return false;
      if (statusFilter !== "all" && o.paymentStatus !== statusFilter) return false;
      if (modeFilter !== "all" && o.paymentMode !== modeFilter) return false;
      return true;
    });
  }, [orders, q, statusFilter, modeFilter]);

  const filtersActive = statusFilter !== "all" || modeFilter !== "all" || q.trim() !== "";

  const stats = useMemo(() => {
    const total = filtered.reduce((s, o) => s + (o.totalFinal || 0), 0);
    const paid = filtered.reduce((s, o) => s + (o.totalPaid || 0), 0);
    return { count: filtered.length, total, paid };
  }, [filtered]);

  return (
    <div>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="font-serif-display text-2xl sm:text-3xl text-stone-800">Commandes</h1>
          <p className="text-stone-500 mt-1 text-sm sm:text-base">Chaque commande = une soumission d&apos;inscription complète, avec tout son contexte (famille, élèves, tarif, paiement, consentements).</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Rechercher famille, email, ville, n° commande…"
              data-testid="orders-search"
              className="bg-white border border-stone-200 rounded-lg pl-9 pr-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blush min-w-[260px]"
            />
          </div>
        </div>
      </div>

      {/* Barre de filtres : statut + mode de paiement */}
      <div className="mt-4 flex items-center gap-2 flex-wrap" data-testid="orders-filter-bar">
        <span className="inline-flex items-center gap-1.5 text-xs text-stone-500 mr-1">
          <Filter size={14} className="text-deep-rose" /> Filtrer
        </span>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          data-testid="orders-filter-status"
          className="bg-white border border-stone-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blush"
        >
          <option value="all">Tous les statuts</option>
          <option value="paid">Payée</option>
          <option value="sepa_active">SEPA en cours (prélèvements)</option>
          <option value="sepa_scheduled">SEPA à prélever (mandat signé)</option>
          <option value="partial">Partiel</option>
          <option value="pending">En attente de paiement</option>
          <option value="failed">Échec</option>
          <option value="refunded">Remboursée</option>
          <option value="partially_refunded">Remboursée en partie</option>
        </select>
        <select
          value={modeFilter}
          onChange={(e) => setModeFilter(e.target.value)}
          data-testid="orders-filter-mode"
          className="bg-white border border-stone-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blush"
        >
          <option value="all">Tous les modes de paiement</option>
          <option value="card">Carte bancaire (en ligne)</option>
          <option value="monthly">SEPA 8x (auto)</option>
          <option value="cheque">Chèque</option>
          <option value="especes">Espèces</option>
        </select>
        {filtersActive && (
          <button
            onClick={() => { setStatusFilter("all"); setModeFilter("all"); setQ(""); }}
            data-testid="orders-filter-reset"
            className="inline-flex items-center gap-1 text-xs text-stone-600 hover:text-deep-rose bg-stone-100 hover:bg-blush-light rounded-full px-3 py-1.5 transition-colors"
          >
            <X size={12} /> Réinitialiser
          </button>
        )}
      </div>

      {!loading && (
        <div className="mt-4">
          <FinancialSummary
            attendu={stats.total}
            encaisse={stats.paid}
            testid="orders-financial-summary"
          />
          <div className="mt-2 text-xs text-stone-500 text-right">
            <strong>{stats.count}</strong> commande{stats.count > 1 ? "s" : ""} {filtersActive ? `affichée${stats.count > 1 ? "s" : ""} (sur ${orders.length})` : "au total"}
          </div>
        </div>
      )}

      {loading ? (
        <div className="mt-6 text-stone-500 p-8 text-center">Chargement...</div>
      ) : filtered.length === 0 ? (
        <div className="mt-6 bg-white rounded-2xl p-8 text-center text-stone-500 text-sm">Aucune commande trouvée.</div>
      ) : (
        <div className="mt-6 space-y-3">
          {filtered.map((o) => (
            <button
              key={o.id}
              onClick={() => navigate(`/admin/orders/${o.id}`)}
              data-testid={`order-row-${o.id}`}
              className="w-full text-left bg-white rounded-2xl shadow-sm p-4 flex items-center gap-3 hover:bg-blush-light/30 transition-colors flex-wrap"
            >
              <div className="w-10 h-10 rounded-full bg-blush-light text-deep-rose flex items-center justify-center shrink-0">
                <User size={16} />
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-serif-display text-stone-800">{o.parentName || "—"}</span>
                  <span className="text-xs text-stone-700 font-mono bg-blush-light/70 rounded-full px-2 py-0.5">{o.orderNumber || `#${(o.id || "").slice(0, 8)}`}</span>
                  {o.sepaStuck && (
                    <span
                      title="Abonnement SEPA à vérifier — cliquer pour ouvrir le diagnostic"
                      onClick={(e) => { e.stopPropagation(); navigate(`/admin/sepa-diagnostic?tx=${o.stripeTxId || ""}`); }}
                      data-testid={`order-sepa-warn-${o.id}`}
                      className="inline-flex items-center gap-1 text-[10px] font-medium text-white bg-amber-500 hover:bg-amber-600 rounded-full px-2 py-0.5 cursor-pointer"
                    >
                      <AlertTriangle size={10} /> SEPA
                    </span>
                  )}
                </div>
                <div className="text-stone-500 text-xs mt-0.5 flex items-center gap-2 flex-wrap">
                  <span>{o.familyEmail}</span>
                  {o.city && <span>· {o.city}</span>}
                  <span>· {fmtDate(o.createdAt)}</span>
                  {Array.isArray(o.students) && <span>· {o.students.length} élève{o.students.length > 1 ? "s" : ""}</span>}
                  {Array.isArray(o.enrollmentIds) && <span>· {o.enrollmentIds.length} créneau{o.enrollmentIds.length > 1 ? "x" : ""}</span>}
                  {o.consents?.cseAttestation && <span className="inline-flex items-center gap-0.5 text-amber-700">· ★ CSE</span>}
                </div>
              </div>
              <div className="text-right shrink-0">
                <div className="font-serif-display text-stone-800">{fmtEur(o.totalFinal)}</div>
                <div className="mt-1 flex items-center gap-2 justify-end">
                  <span className="text-stone-500 text-xs">{MODE_LABELS[o.paymentMode] || o.paymentMode}</span>
                  <StatusBadge status={o.paymentStatus} />
                </div>
              </div>
              <ChevronRight size={16} className="text-stone-400 shrink-0" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default OrdersAdmin;
